import type { StrategyCanvas } from '@/stores/prioritization/strategyCanvasStore';
import type { Strategy } from '@/types/prioritization/strategy';
import type { Goal, StrategicObjective } from '@/lib/strategyAlignment';
import type { OutcomeDriver } from '@/stores/prioritization/outcomeDriverStore';

const CANVAS_TEXT_BLOCKS: { key: keyof StrategyCanvas; label: string }[] = [
  { key: 'vision', label: 'Vision' },
  { key: 'purpose', label: 'Purpose' },
  { key: 'mission', label: 'Mission' },
  { key: 'guidingPrinciples', label: 'Guiding Principles' },
  { key: 'competencies', label: 'Core Competencies' },
  { key: 'assumptions', label: 'Assumptions' },
];

export interface StrategyCanvasPrintBodyProps {
  canvas: StrategyCanvas;
  linkedStrategies: Strategy[];
  objectives: StrategicObjective[];
  goals: Goal[];
  outcomeDrivers: OutcomeDriver[];
}

export function StrategyCanvasPrintBody({
  canvas,
  linkedStrategies,
  objectives,
  goals,
  outcomeDrivers,
}: StrategyCanvasPrintBodyProps) {
  const title = canvas.title?.trim() || 'Untitled Canvas';

  return (
    <div className="space-y-8 text-[13px] leading-relaxed">
      <div className="print-field-block border-b border-[var(--rule)] pb-5 space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-[var(--text-pri)] print:text-black">{title}</h1>
        <p className="text-[11px] uppercase tracking-wider text-[var(--text-ter)] print:text-[var(--text-ter)]">
          Strategy canvas · Priorities.ai print view · {new Date().toLocaleString()}
        </p>
      </div>

      <div className="space-y-6">
        {CANVAS_TEXT_BLOCKS.map(({ key, label }) => {
          const raw = canvas[key];
          const text = typeof raw === 'string' && raw.trim().length ? raw.trim() : '—';
          return (
            <section key={String(key)} className="print-field-block space-y-1.5">
              <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] print:text-[var(--text-sec)] border-b border-[var(--rule)] pb-1">
                {label}
              </h2>
              <div className="whitespace-pre-wrap text-[var(--text-pri)] print:text-black">{text}</div>
            </section>
          );
        })}
      </div>

      <section className="print-field-block space-y-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] print:text-[var(--text-sec)] border-b border-[var(--rule)] pb-1">
          Linked strategies
        </h2>
        {linkedStrategies.length ? (
          <ul className="list-disc pl-5 space-y-1 text-[var(--text-pri)] print:text-black">
            {linkedStrategies.map((s) => (
              <li key={s.id}>{s.name}</li>
            ))}
          </ul>
        ) : (
          <p className="text-[var(--text-sec)] print:text-[var(--text-sec)]">No strategies linked to this canvas.</p>
        )}
      </section>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
        <section className="print-field-block space-y-2">
          <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] print:text-[var(--text-sec)] border-b border-[var(--rule)] pb-1">
            Objectives
          </h2>
          {objectives.length ? (
            <ul className="list-disc pl-5 space-y-1 text-[var(--text-pri)] print:text-black">
              {objectives.map((o) => (
                <li key={o.id}>{o.name}</li>
              ))}
            </ul>
          ) : (
            <p className="text-[var(--text-sec)] print:text-[var(--text-sec)]">None on this canvas.</p>
          )}
        </section>
        <section className="print-field-block space-y-2">
          <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] print:text-[var(--text-sec)] border-b border-[var(--rule)] pb-1">
            Goals
          </h2>
          {goals.length ? (
            <ul className="list-disc pl-5 space-y-1 text-[var(--text-pri)] print:text-black">
              {goals.map((g) => (
                <li key={g.id}>{g.name}</li>
              ))}
            </ul>
          ) : (
            <p className="text-[var(--text-sec)] print:text-[var(--text-sec)]">None on this canvas.</p>
          )}
        </section>
      </div>

      <section className="print-field-block space-y-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] print:text-[var(--text-sec)] border-b border-[var(--rule)] pb-1">
          Outcome drivers
        </h2>
        {outcomeDrivers.length ? (
          <ul className="list-disc pl-5 space-y-1 text-[var(--text-pri)] print:text-black">
            {outcomeDrivers.map((d) => (
              <li key={d.id}>{d.title?.trim() ? d.title : 'Untitled driver'}</li>
            ))}
          </ul>
        ) : (
          <p className="text-[var(--text-sec)] print:text-[var(--text-sec)]">No outcome drivers in this workspace yet.</p>
        )}
      </section>
    </div>
  );
}
